import { Component, ChangeDetectionStrategy, Input, Output, EventEmitter, HostBinding, ViewChild } from '@angular/core';
import { MatMenuTrigger } from '@angular/material/menu';
import { MenuBadge, MenuItem, MyItems } from '@frontend-site/main-header/domain';
import { RouterState } from '@frontend-site/shared/router-store';
import { FocusGrabWorkaroundService } from '../focus-grab-workaround.service';
import { MenuItemHighlightService } from './menu-item-highlight.service';

@Component({
    selector: 'p-nav-item',
    templateUrl: './nav-item.component.html',
    styleUrls: ['./nav-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavItemComponent {
    @ViewChild(MatMenuTrigger) menuTrigger!: MatMenuTrigger;
    private timedOutCloser?: ReturnType<typeof setTimeout>;
    private isHighlighted = false;
    isMenuOpen = false;
    isMouseInside = false;

    @Input() set routerState(routerState: RouterState | undefined | null) {
        if (!routerState) return;

        this.calculateIsHighlighted(routerState);
    }

    @Input() item: MenuItem = {} as MenuItem;
    @Input() badge?: MenuBadge;
    @Input() myItems?: MyItems | null;
    @Input() isSubMenu? = false;
    @Output() itemClicked: EventEmitter<MenuItem> = new EventEmitter();
    @Output() badgeClicked: EventEmitter<MenuBadge> = new EventEmitter();
    @Output() triggerClearTimeout: EventEmitter<void> = new EventEmitter();
    @Output() triggerMouseLeave: EventEmitter<void> = new EventEmitter();

    constructor(
        private readonly menuItemHighlightService: MenuItemHighlightService,
        private readonly focusGrabWorkaroundService: FocusGrabWorkaroundService
    ) {}

    @HostBinding('class') get cssClasses(): string {
        return this.item.settings?.cssClasses ?? '';
    }

    @HostBinding('class.active') get isActive(): boolean {
        return this.isHighlighted;
    }

    openInNewTab(menuItem: MenuItem): void {
        window.open(menuItem.routePath, '_blank')?.focus();
    }

    onClick(menuItem: MenuItem): void {
        this.itemClicked.emit(menuItem);
    }

    onBadgeClick(event: Event, badge: MenuBadge): void {
        event.preventDefault();
        event.stopPropagation();
        this.badgeClicked.emit(badge);
    }

    mouseEnter(trigger: MatMenuTrigger): void {
        if (!this.item?.menu?.length) return;
        this.isMouseInside = true;
        this.handleCLearTimeOut();
        this.triggerClearTimeout.emit();
        trigger.openMenu();
        this.isMenuOpen = true;
        this.focusGrabWorkaroundService.restoreFocus();
    }

    handleCLearTimeOut() {
        if (this.timedOutCloser) {
            clearTimeout(this.timedOutCloser);
        }
    }

    mouseLeave(trigger: MatMenuTrigger): void {
        this.isMouseInside = false;
        this.timedOutCloser = setTimeout(() => {
            trigger.closeMenu();
            this.triggerMouseLeave.emit();
            this.focusGrabWorkaroundService.restoreFocus();
        }, 0);
        this.isMenuOpen = false;
    }

    handleChildMouseLeave() {
        if (!this.isMouseInside) {
            this.mouseLeave(this.menuTrigger);
        }
    }

    public menuId(index: number, menu: MenuItem): string | undefined {
        return menu.id;
    }

    private calculateIsHighlighted(routerState: RouterState): void {
        const isCurrentlyHighlighted = this.isHighlighted;
        this.isHighlighted = this.menuItemHighlightService.isHighlighted(routerState, this.item, isCurrentlyHighlighted);
    }
}
